import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { IEnvironment } from '../../../models/environment.model';

import {
  EditableTableRow,
  EditableTableColumn,
} from '../../../models/irembo-editable-table.model';

@Component({
  standalone: false,
  selector: 'irembogov-custom-editable-table',
  templateUrl: './custom-editable-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomEditableTableComponent
  extends FieldType<FieldTypeConfig>
  implements OnDestroy, OnInit
{
  private readonly environment: IEnvironment;
  private readonly subscriptions = new Subscription();

  rows: EditableTableRow[] = [];
  columns: EditableTableColumn[] = [];
  loading = false;

  constructor(
    @Inject('environment') private readonly env: IEnvironment,
    private readonly http: HttpClient,
    private readonly cd: ChangeDetectorRef
  ) {
    super();
    this.environment = env;
  }

  ngOnInit(): void {
    this.rows =
      (this.field.props?.['rows'] as unknown as EditableTableRow[]) || [];
    this.columns =
      (this.field.props?.['columns'] as EditableTableColumn[]) || [];

    if (this.field.props?.disabled) {
      this.formControl.disable();
    }

    if (!this.formControl.value) {
      const initialValue: Record<string, Record<string, any>> = {};
      this.rows.forEach(row => {
        initialValue[row.key] = {};
        this.columns.forEach(column => {
          initialValue[row.key][column.key] = null;
        });
      });
      this.formControl.setValue(initialValue);
    }

    this.subscriptions.add(
      this.field?.options?.fieldChanges
        ?.pipe(
          filter((e: any) => {
            return (
              e.type === 'expressionChanges' &&
              e.field === this.field &&
              e['property'] === 'props.disabled'
            );
          })
        )
        .subscribe((res: any) => {
          if (res.value) {
            this.formControl.disable();
          } else {
            this.formControl.enable();
          }
        })
    );

    this.cd.detectChanges();
  }

  handleCellValueChange(rowKey: string, columnKey: string, value: any): void {
    const currentValue = this.formControl.value || {};

    if (!currentValue[rowKey]) {
      currentValue[rowKey] = {};
    }

    currentValue[rowKey][columnKey] = value;
    this.formControl.setValue(currentValue);
    this.formControl.markAsTouched();
    this.formControl.markAsDirty();
  }

  getCellValue(rowKey: string, columnKey: string): any {
    return this.formControl.value?.[rowKey]?.[columnKey];
  }

  isCellDisabled(row: EditableTableRow, column: EditableTableColumn): boolean {
    return (
      this.formControl.disabled || row.disabled || column.disabled || false
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
